import { PostSortRowsParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useRef } from 'react';

import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import {
	CreateCellClickHandler,
	createCellCssClassRules,
	CreateCellDoubleClickHandler,
	createGridDropDownCommitAfter,
	CustomCellClickAction,
	CustomCellDoubleClickAction,
	GridCellConfig,
	postSortHandler,
	RemoveRowHandler,
	useRowSelectionHandler,
} from 'src/utils/grid/GridHandlerContent';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';

import {
	COLUMN_STATE_KEY,
	createBaseColumnDefs,
	createColumnDefs,
	createDefaultColumnState,
} from './ColumnsDef_SYS2010_R01';
import { SYS2010_R01Dto } from './Dto_SYS2010_R01';
import { useSYS2010_R01CellChange } from './hooks/SYS2010_R01CellChange';
import { useSYS2010_R01Modal } from './hooks/useSYS2010_R01Modal';
import useSYS2010_R01Store from './Store_SYS2010_R01';

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<SYS2010_R01Dto> | null>;
	defaultSYS2010_R01Row: () => SYS2010_R01Dto;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const Table_SYS2010_R01 = (props: Props) => {
	const { clickable, setClickable, gridRef, defaultSYS2010_R01Row, handleInsertClick } = props;

	const {
		SYS2010_R01List, setSYS2010_R01List, updateSYS2010_R01List, removeSYS2010_R01List, deletedSYS2010_R01List, addDeletedSYS2010_R01List,
		selectedSYS2010_R01List, addSelectedSYS2010_R01List, removeSelectedSYS2010_R01List, initSelectedSYS2010_R01List, allSelectedSYS2010_R01List,
		originalSYS2010_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useSYS2010_R01Store();
	const { menuCdList, devStsList, devloperList } = useCommonCodeStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		handleSwitchButtonHandler,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SYS2010_R01List,
		defaultGridData: defaultSYS2010_R01Row(),
		selectedRow: selectedSYS2010_R01List,
		originalGridData: originalSYS2010_R01List,
		updateRow: (index: number, data: SYS2010_R01Dto) => updateSYS2010_R01List(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [''],
		insertedDisabledCells: [''],
		commonDisabledCells: ['menuCd'],
		pointerCells: ['menuNm', 'upMenu', 'useYn', 'displayOnly', 'menuLevel', 'menuSeq', 'devloper', 'queryAut', 'insertAut', 'deleteAut', 'saveAut', 'printAut', 'excelAut', 'devSts', 'remark'],
		insertedPointerCells: ['menuCd'],
		commonPointerCells: [''],
		alignLeftCells: ['devloper', 'devSts'],
	}), []);

	const {
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<SYS2010_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedSYS2010_R01List,
			deletedList: deletedSYS2010_R01List,
			disabledInsertedColumns,
			disabledColumns,
			pointerInsertedColumns,
			pointerColumns,
			alignLeftColumns,
		});

		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [createCellClassRules, selectedSYS2010_R01List, deletedSYS2010_R01List, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns]);

	const commonInputColumns = ['menuCd', 'menuNm', 'useYn', 'displayOnly', 'menuLevel', 'menuSeq', 'devloper', 'queryAut', 'insertAut', 'deleteAut', 'saveAut', 'printAut', 'excelAut', 'devSts', 'manualPage', 'remark'];
	const defaultEditableColumns = ['menuCd', 'menuNm', 'menuLevel', 'menuSeq', 'devloper', 'queryAut', 'insertAut', 'deleteAut', 'saveAut', 'printAut', 'excelAut', 'devSts', 'manualPage', 'remark'];

	const { settingCodeSearchModalProps } = useSYS2010_R01Modal({
		updateSYS2010_R01List,
		gridRef,
	});

	const handleGridDropDownCommitAfter = useMemo(
		() => createGridDropDownCommitAfter<SYS2010_R01Dto>(defaultEditableColumns)
	, [defaultEditableColumns]);

	const defaultRow = defaultSYS2010_R01Row();

	const baseColumnDefs = useMemo(
		() => createBaseColumnDefs({
			indexCellRenderer: (params: SYS2010_R01Dto) => renderIndexCell(
				params,
				(innerParams: SYS2010_R01Dto) => getRowStyleFlags(
					innerParams,
					{
						deletedList: deletedSYS2010_R01List,
						selectedList: selectedSYS2010_R01List,
						dataList: SYS2010_R01List,
					},
				),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
					} else {
						handleInsertClick(e, rowIndex);
					}
				},
				(rowData) => RemoveRowHandler(
					gridRef,
					SYS2010_R01List,
					rowData,
					setClickable,
					removeSYS2010_R01List,
					(valueIndex) => addDeletedSYS2010_R01List([valueIndex.toString()]),
				),
			),
			handleSwitchButtonHandler,
			menuCdList,
			devloperList,
			devStsList,
			updateSYS2010_R01List,
			handleGridDropDownCommitAfter,
			defaultManualPage: defaultRow.manualPage,
		})
	, [renderIndexCell, deletedSYS2010_R01List, selectedSYS2010_R01List, SYS2010_R01List, handleInsertClick, gridRef, setClickable, removeSYS2010_R01List, addDeletedSYS2010_R01List, handleSwitchButtonHandler, menuCdList, devloperList, devStsList, updateSYS2010_R01List, handleGridDropDownCommitAfter, defaultRow.manualPage]);

	const defaultColumnState = useMemo(
		() => createDefaultColumnState(baseColumnDefs)
	, [baseColumnDefs]);

	const columnDefs = useMemo(
		() => createColumnDefs(baseColumnDefs, columnState)
	, [baseColumnDefs, columnState]);

	const { handleCellChange } = useSYS2010_R01CellChange({
		updateSYS2010_R01List,
		originalSYS2010_R01List,
		defaultSYS2010_R01Row: defaultSYS2010_R01Row(),
	});

	const handleRowClick = useRowSelectionHandler({
		dataList: SYS2010_R01List,
		idKey: 'valueIndex',
		selectedList: selectedSYS2010_R01List,
		deletedList: deletedSYS2010_R01List,
		addSelected: addSelectedSYS2010_R01List,
		removeSelected: removeSelectedSYS2010_R01List,
		clickable,
		setClickable,
	});

	const cellEventHandlers = useDetailTableEvents({
		detailList: SYS2010_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		},
	});

	const cellClickActions: CustomCellClickAction<SYS2010_R01Dto>[] = useMemo(() => [
		{
			validate: (field) => commonInputColumns.includes(field),
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); },
		},
	], [commonInputColumns]);
	const handleCellClick = CreateCellClickHandler(SYS2010_R01List, 'valueIndex', cellClickActions);

	const cellDoubleClickActions: CustomCellDoubleClickAction<SYS2010_R01Dto>[] = useMemo(() => [
		{
			validate: (field) => field === 'upMenu',
			action: (e, foundData) => {
				settingCodeSearchModalProps('상위메뉴코드', foundData.upMenu!, false, 'upMenu', foundData, foundData.valueIndex!);
			},
		},
	], [settingCodeSearchModalProps]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(SYS2010_R01List, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<SYS2010_R01Dto>) => {
		postSortHandler(params, SYS2010_R01List, originalSYS2010_R01List, setSYS2010_R01List);
	}, [SYS2010_R01List, originalSYS2010_R01List, setSYS2010_R01List]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		menuCd: SYS2010_R01List.length.toLocaleString() + '건',
		editable: false,
	}]), [SYS2010_R01List.length]);

	return (
		<GridContextWrapper<SYS2010_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setSYS2010_R01List}
			initSelected={initSelectedSYS2010_R01List}
			allSelected={allSelectedSYS2010_R01List}
			defaultRowData={defaultSYS2010_R01Row()}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
		>
			<Grid_Excel<SYS2010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				rowClassRules={createRowClassRules<SYS2010_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedSYS2010_R01List,
					deletedList: deletedSYS2010_R01List,
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}
				onPostSortRows={handlePostSort}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
